import React, { useMemo } from "react";
import ScrollbarWrapper from "../../../../../components/ACS-Dashboard/ACS-Blocks/ScrollbarWrapper";
import { AlertTriangle, Eye, Truck, Warehouse, X } from "lucide-react";

const TOLERANCE_MINUTES = 15;

const STATUS_STYLES = {
  LATE: {
    chip: "text-red-600 dark:text-red-400",
    dot: "bg-red-500",
    ring: "ring-red-500/90 dark:ring-red-500/70",
    glow: "shadow-[0_0_10px_rgba(239,68,68,0.4)] dark:shadow-[0_0_12px_rgba(239,68,68,0.3)]",
    event: "text-red-600 dark:text-red-400",
    label: "Late",
  },
  EARLY: {
    chip: "text-blue-600 dark:text-blue-400",
    dot: "bg-blue-500",
    ring: "ring-blue-500/90 dark:ring-blue-500/70",
    glow: "shadow-[0_0_10px_rgba(59,130,246,0.4)] dark:shadow-[0_0_12px_rgba(59,130,246,0.3)]",
    event: "text-blue-600 dark:text-blue-400",
    label: "Early",
  },
  ON_TIME: {
    chip: "text-lime-600 dark:text-lime-400",
    dot: "bg-emerald-500",
    ring: "ring-emerald-500/90 dark:ring-emerald-500/70",
    glow: "shadow-[0_0_10px_rgba(16,185,129,0.4)] dark:shadow-[0_0_12px_rgba(16,185,129,0.3)]",
    event: "text-lime-600 dark:text-lime-400",
    label: "On-time",
  },
  PENDING: {
    chip: "text-gray-400 dark:text-gray-500",
    dot: "bg-gray-400",
    ring: "ring-gray-300 dark:ring-gray-600",
    glow: "shadow-[0_0_8px_rgba(148,163,184,0.35)] dark:shadow-[0_0_10px_rgba(71,85,105,0.35)]",
    event: "text-gray-400 dark:text-gray-500",
    label: "Pending",
  },
};

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = parseDate(value);
  if (!d) return "--";
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getMilestoneStatus = (planned, actual) => {
  const plannedDate = parseDate(planned);
  const actualDate = parseDate(actual);

  if (!plannedDate || !actualDate) {
    return { key: "PENDING", deltaMinutes: null };
  }

  const deltaMinutes = Math.round(
    (actualDate.getTime() - plannedDate.getTime()) / 60000
  );

  if (deltaMinutes > TOLERANCE_MINUTES) {
    return { key: "LATE", deltaMinutes };
  }

  if (deltaMinutes < -TOLERANCE_MINUTES) {
    return { key: "EARLY", deltaMinutes };
  }

  return { key: "ON_TIME", deltaMinutes };
};

const buildStopMilestones = (stops) => {
  const normalizedStops = Array.isArray(stops) ? stops : [];

  return normalizedStops.map((stop, index) => {
    const milestones = [
      {
        key: "gateIn",
        label: "Gate In",
        planned: stop?.scheduledGateIn,
        actual: stop?.actualGateIn,
      },
      {
        key: "gateOut",
        label: "Gate Out",
        planned: stop?.scheduledGateOut,
        actual: stop?.actualGateOut,
      },
    ].map((event) => {
      const status = getMilestoneStatus(event.planned, event.actual);
      const actualDate = parseDate(event.actual);
      return {
        ...event,
        status,
        actualDate,
      };
    });

    const lastReportedMilestone = milestones
      .filter((event) => event.actualDate)
      .sort((a, b) => b.actualDate.getTime() - a.actualDate.getTime())[0];

    const lastReportedStatusKey = lastReportedMilestone?.status?.key || "PENDING";

    return {
      stop,
      index,
      milestones,
      lastReportedMilestone,
      lastReportedStatusKey,
      appointmentMissing: !parseDate(stop?.appointmentStart),
    };
  });
};

const ShipmentVisibility = ({ isOpen, onClose, shipmentStops = [] }) => {
  const stopMilestones = useMemo(
    () => buildStopMilestones(shipmentStops),
    [shipmentStops]
  );

  const lastReportedEvent = useMemo(() => {
    const events = stopMilestones
      .flatMap((entry) =>
        entry.milestones
          .filter((milestone) => milestone.actualDate)
          .map((milestone) => ({
            stopIndex: entry.index,
            stopName: entry.stop?.stopName,
            location: `${entry.stop?.city || ""}, ${entry.stop?.state || ""}`,
            milestone,
          }))
      )
      .sort(
        (a, b) =>
          b.milestone.actualDate.getTime() - a.milestone.actualDate.getTime()
      );

    return events[0] || null;
  }, [stopMilestones]);

  const currentStatusKey = lastReportedEvent?.milestone?.status?.key || "PENDING";
  const currentStatusStyle =
    STATUS_STYLES[currentStatusKey] || STATUS_STYLES.PENDING;

  return (
    <div className={`fixed inset-0 z-40 ${isOpen ? "" : "pointer-events-none"}`}>
      <div
        className={`absolute inset-0 bg-slate-900/30 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />

      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-[46rem] transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col overflow-hidden border-l border-gray-300 bg-white shadow-2xl ring-1 ring-black/5 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:ring-acs-dark-border">
          <header className="flex items-center justify-between gap-2 border-b border-gray-300 bg-white px-fluid-3 py-fluid-2 text-gray-900 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-gray-50 text-gray-700 dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-subtext">
                <Eye size={20} />
              </div>
              <div className="flex items-center gap-8">
                <button
                  type="button"
                  className="border-b-2 border-gray-700 pb-1 text-fluid-sm font-medium dark:border-acs-dark-text"
                >
                  Milestone View
                </button>
                <button
                  type="button"
                  className="pb-1 text-fluid-sm font-medium text-gray-500 dark:text-acs-dark-subtext"
                >
                  Reported Events
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-600 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/60 dark:text-acs-dark-subtext dark:hover:bg-acs-dark-hover"
            >
              <X size={18} />
            </button>
          </header>

          <ScrollbarWrapper className="flex-1 bg-white p-fluid-3 font-roboto text-fluid-base dark:bg-acs-dark-surface">
            <div className="mb-fluid-3 px-fluid-1">
              <p className="text-xs font-semibold text-gray-800 dark:text-acs-dark-text">
                Current On Time Status:
                <span className={`ml-1.5 text-xs font-semibold ${currentStatusStyle.chip}`}>
                  {currentStatusStyle.label}
                </span>
              </p>
            </div>

            <ol className="space-y-fluid-3">
              {stopMilestones.map((entry, idx) => {
                const stop = entry.stop || {};
                const stopStyle =
                  STATUS_STYLES[entry.lastReportedStatusKey] || STATUS_STYLES.PENDING;
                const isLast = idx === stopMilestones.length - 1;

                return (
                  <li key={stop.id || stop.stopNumber || idx} className="relative pl-14">
                    {!isLast ? (
                      <span className="absolute left-6 top-12 h-[calc(100%-1rem)] w-px bg-gray-600 dark:bg-acs-dark-border" />
                    ) : null}

                    <span
                      className={`absolute left-1 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-white bg-gray-100 text-gray-800 ring-2 dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-subtext ${stopStyle.ring} ${stopStyle.glow}`}
                    >
                      <Warehouse size={16} />
                    </span>

                    <div className="p-fluid-1">
                      <div className="flex flex-wrap items-start justify-between gap-fluid-1.5">
                        <div>
                          <p className="text-[1.5rem] leading-[1.15] font-semibold text-gray-800 dark:text-acs-dark-text">
                            {stop.stopName || `Stop ${idx + 1}`}
                          </p>
                        </div>

                        {entry.appointmentMissing ? (
                          <div className="inline-flex items-center gap-1 text-xs font-medium text-red-500 dark:text-red-400">
                            Appointment Missing
                            <AlertTriangle size={14} className="-mt-px" />
                          </div>
                        ) : (
                          <p className="text-xs text-gray-700 dark:text-acs-dark-subtext">
                            Appointment Time: {formatDateTime(stop.appointmentStart)}
                          </p>
                        )}
                      </div>

                      <p className="mt-0.5 text-[1.35rem] leading-[1.15] text-gray-700 dark:text-acs-dark-subtext">
                        {stop.city || "--"}, {stop.state || "--"}
                      </p>

                      <div className="mt-fluid-1.5 pl-fluid-2">
                        {entry.milestones.map((event) => {
                          const eventStyle =
                            STATUS_STYLES[event.status.key] || STATUS_STYLES.PENDING;
                          const eventIsLast = event.key === "gateOut";
                          return (
                            <div
                              key={event.key}
                              className={`relative pl-8 ${eventIsLast ? "" : "pb-fluid-2"}`}
                            >
                              {!eventIsLast ? (
                                <span className="absolute left-[9px] top-6 h-[calc(100%-0.6rem)] w-px bg-gray-700 dark:bg-acs-dark-border" />
                              ) : null}

                              <Truck size={15} className="absolute left-0 top-1 text-black dark:text-acs-dark-text" />

                              <p className={`text-fluid-base font-semibold ${eventStyle.event}`}>
                                {event.label} ({eventStyle.label})
                              </p>

                              <p className="text-xs text-gray-700 dark:text-acs-dark-subtext">
                                Planned: {formatDateTime(event.planned)}
                              </p>
                              <p className="text-xs text-gray-700 dark:text-acs-dark-subtext">
                                Actual: {formatDateTime(event.actual)}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </ScrollbarWrapper>
        </div>
      </aside>
    </div>
  );
};

export default ShipmentVisibility;
